import { uploadAgentAttachment } from '@libs/rest-client';
import { genMsg, useLocalizedContents } from '@ps-smart-cp/ppfw-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AttachmentDraft } from '../models/attachment';
import {
  type AttachmentRejectionMessages,
  attachmentRejectionMessage,
  isImageMimeType,
  partitionSelectedAttachments,
} from '../services/attachment';

/**
 * 一覧の正本は ref 側で state はその写し。setState の updater を純粋に保つためで、
 * StrictMode の二重呼び出しで objectURL 生成と upload が 2 回走るのを防ぐ。
 */
export function useAttachments() {
  const draftsRef = useRef<AttachmentDraft[]>([]);
  const [drafts, setDrafts] = useState<AttachmentDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { msg } = useLocalizedContents();

  const rejectionMessages = useMemo<AttachmentRejectionMessages>(
    () => ({ unsupportedType: msg.efw12013, tooLarge: msg.efw54003, tooMany: msg.efw12009 }),
    [msg],
  );

  const commit = useCallback((next: AttachmentDraft[]) => {
    draftsRef.current = next;
    setDrafts(next);
  }, []);

  const releasePreview = useCallback((previewUrl: string | undefined) => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, []);

  // unmount 時に取りこぼしを revoke する（送信済み / 削除済みは既に revoke されている）
  useEffect(
    () => () => {
      for (const draft of draftsRef.current) {
        if (draft.previewUrl) {
          URL.revokeObjectURL(draft.previewUrl);
        }
      }
      draftsRef.current = [];
    },
    [],
  );

  const addFiles = useCallback(
    (files: readonly File[]) => {
      const { accepted, rejection } = partitionSelectedAttachments(files, draftsRef.current.length);
      setError(rejection ? attachmentRejectionMessage(rejection, rejectionMessages) : null);
      if (accepted.length === 0) {
        return;
      }

      const added = accepted.map<[AttachmentDraft, File]>((file) => [
        {
          id: crypto.randomUUID(),
          fileName: file.name,
          mimeType: file.type,
          previewUrl: isImageMimeType(file.type) ? URL.createObjectURL(file) : undefined,
          status: 'uploading',
        },
        file,
      ]);
      commit([...draftsRef.current, ...added.map(([draft]) => draft)]);

      for (const [draft, file] of added) {
        void uploadAgentAttachment(file)
          .then((uploaded) => {
            commit(
              draftsRef.current.map((item) =>
                item.id === draft.id ? { ...item, status: 'ready', key: uploaded.key } : item,
              ),
            );
          })
          .catch(() => {
            // 失敗した1件は落として送信をブロックしない（同じファイルを選び直せる）
            commit(draftsRef.current.filter((item) => item.id !== draft.id));
            releasePreview(draft.previewUrl);
            setError(genMsg(msg.efw54001, draft.fileName));
          });
      }
    },
    [commit, msg, rejectionMessages, releasePreview],
  );

  const removeDraft = useCallback(
    (id: string) => {
      setError(null);
      const target = draftsRef.current.find((draft) => draft.id === id);
      if (target) {
        releasePreview(target.previewUrl);
      }
      commit(draftsRef.current.filter((draft) => draft.id !== id));
    },
    [commit, releasePreview],
  );

  const clearDrafts = useCallback(() => {
    setError(null);
    for (const draft of draftsRef.current) {
      releasePreview(draft.previewUrl);
    }
    commit([]);
  }, [commit, releasePreview]);

  return { drafts, error, addFiles, removeDraft, clearDrafts };
}
